/**
 * CoachPlaybook - Color Settings Component
 * Version: 0.3.42
 * 
 * Customizable colors for players and lines (color blindness accessibility)
 */

import React, { useState, useEffect } from 'react';

// Default colors
export const DEFAULT_POSITION_COLORS = {
  PG: '#3b82f6', // Blue
  SG: '#10b981', // Green
  SF: '#f59e0b', // Yellow/Amber
  PF: '#f97316', // Orange
  C: '#8b5cf6'   // Purple
};

export const DEFAULT_LINE_COLORS = {
  movement: '#1f2937', // Dark gray
  pass: '#1f2937',     // Dark gray
  dribble: '#1f2937',  // Dark gray
  cut: '#1f2937',      // Dark gray
  screen: '#374151'    // Gray
};

// Color-blind friendly presets
export const COLOR_PRESETS = {
  default: {
    name: 'Default',
    positions: DEFAULT_POSITION_COLORS,
    lines: DEFAULT_LINE_COLORS
  },
  deuteranopia: {
    name: 'Deuteranopia (Red-Green)',
    positions: {
      PG: '#0077bb', // Blue
      SG: '#33bbee', // Cyan
      SF: '#ee7733', // Orange
      PF: '#cc3311', // Red
      C: '#009988'   // Teal
    },
    lines: {
      movement: '#332288',
      pass: '#332288',
      dribble: '#332288',
      cut: '#332288',
      screen: '#555555'
    }
  },
  protanopia: {
    name: 'Protanopia (Red-Green)',
    positions: {
      PG: '#4477aa', // Blue
      SG: '#66ccee', // Cyan
      SF: '#ccbb44', // Yellow
      PF: '#ee6677', // Pink
      C: '#aa3377'   // Purple
    },
    lines: {
      movement: '#225588',
      pass: '#225588',
      dribble: '#225588',
      cut: '#225588',
      screen: '#555555'
    }
  },
  tritanopia: {
    name: 'Tritanopia (Blue-Yellow)',
    positions: {
      PG: '#d55e00', // Vermillion
      SG: '#cc79a7', // Pink
      SF: '#0072b2', // Blue
      PF: '#f0e442', // Yellow
      C: '#009e73'   // Teal
    },
    lines: {
      movement: '#000000',
      pass: '#000000',
      dribble: '#000000',
      cut: '#000000',
      screen: '#555555'
    }
  },
  highContrast: {
    name: 'High Contrast',
    positions: {
      PG: '#0000ff', // Pure blue
      SG: '#00ff00', // Pure green
      SF: '#ffff00', // Pure yellow
      PF: '#ff0000', // Pure red
      C: '#ff00ff'   // Magenta
    },
    lines: {
      movement: '#000000',
      pass: '#000000',
      dribble: '#000000',
      cut: '#000000',
      screen: '#000000'
    }
  }
};

// Load colors from localStorage
export const loadColorSettings = () => {
  try {
    const saved = localStorage.getItem('coachplaybook-colors');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.warn('Failed to load color settings:', e);
  }
  return {
    positions: { ...DEFAULT_POSITION_COLORS },
    lines: { ...DEFAULT_LINE_COLORS }
  };
};

// Save colors to localStorage
export const saveColorSettings = (colors) => {
  try {
    localStorage.setItem('coachplaybook-colors', JSON.stringify(colors));
  } catch (e) {
    console.warn('Failed to save color settings:', e);
  }
};

const ColorSettings = ({ isOpen, onClose, onColorsChange }) => {
  const [colors, setColors] = useState(loadColorSettings);
  const [activePreset, setActivePreset] = useState('custom');
  
  useEffect(() => {
    if (isOpen) {
      setColors(loadColorSettings());
    }
  }, [isOpen]);
  
  const handlePositionColorChange = (position, color) => {
    const newColors = {
      ...colors,
      positions: { ...colors.positions, [position]: color }
    };
    setColors(newColors);
    setActivePreset('custom');
  };
  
  const handleLineColorChange = (lineType, color) => {
    const newColors = {
      ...colors,
      lines: { ...colors.lines, [lineType]: color }
    };
    setColors(newColors);
    setActivePreset('custom');
  };
  
  const applyPreset = (presetKey) => {
    const preset = COLOR_PRESETS[presetKey];
    if (preset) {
      setColors({
        positions: { ...preset.positions },
        lines: { ...preset.lines }
      });
      setActivePreset(presetKey);
    }
  };
  
  const handleSave = () => {
    saveColorSettings(colors);
    if (onColorsChange) onColorsChange(colors);
    onClose();
  };
  
  const handleReset = () => {
    const defaultColors = {
      positions: { ...DEFAULT_POSITION_COLORS },
      lines: { ...DEFAULT_LINE_COLORS }
    };
    setColors(defaultColors);
    setActivePreset('default');
  };
  
  if (!isOpen) return null;
  
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>🎨 Color Settings</h2>
          <button style={styles.closeButton} onClick={onClose}>×</button>
        </div>
        
        <div style={styles.content}>
          {/* Presets */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Color Blind Presets</h3>
            <div style={styles.presetGrid}>
              {Object.entries(COLOR_PRESETS).map(([key, preset]) => (
                <button
                  key={key}
                  style={{
                    ...styles.presetButton,
                    ...(activePreset === key ? styles.presetButtonActive : {})
                  }}
                  onClick={() => applyPreset(key)}
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>
          
          {/* Position Colors */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Player Position Colors</h3>
            <div style={styles.colorGrid}>
              {Object.entries(colors.positions).map(([pos, color]) => (
                <div key={pos} style={styles.colorRow}>
                  <span style={styles.colorLabel}>{pos}</span>
                  <input
                    type="color"
                    value={color}
                    onChange={e => handlePositionColorChange(pos, e.target.value)}
                    style={styles.colorInput}
                  />
                  <span style={styles.colorHex}>{color}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Line Colors */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Line Colors</h3>
            <div style={styles.colorGrid}>
              {Object.entries(colors.lines).map(([lineType, color]) => (
                <div key={lineType} style={styles.colorRow}>
                  <span style={styles.colorLabel}>{lineType.charAt(0).toUpperCase() + lineType.slice(1)}</span>
                  <input
                    type="color"
                    value={color}
                    onChange={e => handleLineColorChange(lineType, e.target.value)}
                    style={styles.colorInput}
                  />
                  <span style={styles.colorHex}>{color}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Preview */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Preview</h3>
            <div style={styles.preview}>
              <svg viewBox="0 0 200 60" style={styles.previewSvg}>
                {/* Players */}
                {Object.entries(colors.positions).map(([pos, color], idx) => (
                  <g key={pos}>
                    <circle cx={20 + idx * 35} cy={20} r="12" fill={color} fillOpacity="0.6" stroke={color} strokeWidth="2" />
                    <text x={20 + idx * 35} y={24} textAnchor="middle" fill="#fff" fontSize="10" fontWeight="bold">{pos}</text>
                  </g>
                ))}
                {/* Lines */}
                <line x1="10" y1="50" x2="60" y2="50" stroke={colors.lines.movement} strokeWidth="2" strokeDasharray="4,3" />
                <text x="35" y="58" textAnchor="middle" fill="#666" fontSize="8">Move</text>
                <line x1="70" y1="50" x2="120" y2="50" stroke={colors.lines.pass} strokeWidth="2" />
                <text x="95" y="58" textAnchor="middle" fill="#666" fontSize="8">Pass</text>
              </svg>
            </div>
          </div>
        </div>
        
        <div style={styles.footer}>
          <button style={styles.resetButton} onClick={handleReset}>Reset to Default</button>
          <div style={styles.footerRight}>
            <button style={styles.cancelButton} onClick={onClose}>Cancel</button>
            <button style={styles.saveButton} onClick={handleSave}>Save Colors</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #e5e7eb'
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#1f2937'
  },
  closeButton: {
    width: '32px',
    height: '32px',
    border: 'none',
    backgroundColor: '#f3f4f6',
    borderRadius: '6px',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#6b7280'
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '16px 20px'
  },
  section: {
    marginBottom: '20px'
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '12px',
    margin: '0 0 12px 0'
  },
  presetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '8px'
  },
  presetButton: {
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    backgroundColor: '#fff',
    fontSize: '12px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.2s'
  },
  presetButtonActive: {
    borderColor: '#3b82f6',
    backgroundColor: '#eff6ff',
    color: '#1d4ed8'
  },
  colorGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  colorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  colorLabel: {
    width: '80px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151'
  },
  colorInput: {
    width: '40px',
    height: '32px',
    padding: '2px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  colorHex: {
    fontSize: '12px',
    fontFamily: 'monospace',
    color: '#6b7280'
  },
  preview: {
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    padding: '12px'
  },
  previewSvg: {
    width: '100%',
    height: '70px'
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb'
  },
  footerRight: {
    display: 'flex',
    gap: '8px'
  },
  resetButton: {
    padding: '10px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    backgroundColor: '#fff',
    fontSize: '13px',
    cursor: 'pointer',
    color: '#6b7280'
  },
  cancelButton: {
    padding: '10px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    backgroundColor: '#fff',
    fontSize: '13px',
    cursor: 'pointer'
  },
  saveButton: {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer'
  }
};

export default ColorSettings;
